import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useAuth } from '../../context/auth-context';
import axios from 'axios';
import { rootUrl } from '../../helpers';
import DataTable from 'react-data-table-component';
import { 
  FaPlus, 
  FaSpinner, 
  FaSearch, 
  FaTimes, 
  FaEye, 
  FaEdit, 
  FaBuilding,
  FaFilter,
  FaMapMarkerAlt,
  FaUser,
  FaDollarSign,
  FaList
} from 'react-icons/fa';
import Modal from '../../components/Modal';
import { toast } from 'react-toastify';
import { customStyles } from '../../styles';
import { useOutletContext } from 'react-router-dom';


function Properties() {
   
  const {allData, setAllData} = useOutletContext();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all'); // 'all', 'property_no', 'owner', 'location'
  const [form, setForm] = useState({
    property_no: '',
    type: 'RES',
    acc_name: '',
    owner_id: '',
    location: '',
    street: '',
    description: '',
    annual_payable: '',
    opening: '',
    rate_value: '',
    land_value: '',
    land_ext: '',
    improvements: '',
    machinery: '',
    remarks: '',
    coordinates: [] // Array of {lat: number, lng: number} objects
  });
 
  const [editingProperty, setEditingProperty] = useState(null);
  const {user} = useAuth();

  const getProperties = async () => {
    try {
      setLoading(true);
      const response = await axios.get(rootUrl(`/getProperties/${user.id}`));
      setProperties(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }

  useEffect(()=>{
    getProperties();
  }, []);

  const openDetails = (row) => {
    setSelectedProperty(row);
    setModalOpen(true);
  };

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      property_no: '',
      type: 'RES',
      acc_name: '',
      owner_id: '',
      location: '',
      street: '',
      description: '',
      annual_payable: '',
      opening: '',
      rate_value: '',
      land_value: '',
      land_ext: '',
      improvements: '',
      machinery: '',
      remarks: '',
      coordinates: [{ lat: '', lng: '' }] // Start with one coordinate pair
    });
    setEditingProperty(null);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    // Basic validation
    if (!form.property_no || !form.acc_name || !form.type || !form.owner_id) {
      toast.error('Please fill Property No, Account, Type, and Owner');
      return;
    }
    
    // Validate coordinates - need at least 4 valid pairs
    const validCoords = form.coordinates.filter(coord => 
      coord.lat && coord.lng && !isNaN(parseFloat(coord.lat)) && !isNaN(parseFloat(coord.lng))
    );
    
    // if (validCoords.length < 4) {
    //   toast.error('Please provide at least 4 valid coordinate pairs for the property boundary');
    //   return;
    // }
    
    try {
      setSaving(true);
      const coordinatesString = validCoords.length == 0? '' : validCoords.map(coord => `${coord.lat},${coord.lng}`).join(';');
      const payload = { 
        ...form, 
        coordinates: coordinatesString,
        uid: user.id,
        owner_id: form.owner_id
      };
      
      if (editingProperty) {
        payload.id = editingProperty.id;
        await axios.post(rootUrl('/updateProperty'), payload);
        toast.success('Property updated');
      } else {
        await axios.post(rootUrl('/addProperty'), payload);
        toast.success('Property added');
      }
      
      setAddOpen(false);
      resetForm();
      getProperties();
    } catch (err) {
      toast.error(editingProperty ? 'Failed to update property' : 'Failed to add property');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (property) => {
    // Parse coordinates from string to array
    const coords = property.coordinates ? 
      property.coordinates.split(';').map(pair => {
        const [lat, lng] = pair.split(',');
        return { lat: lat?.trim() || '', lng: lng?.trim() || '' };
      }) : [{ lat: '', lng: '' }];
    
    const ownerId = property.uid || property.owner_id;
    console.log('Editing property:', property);
    console.log('Owner ID:', ownerId);
    console.log('Available owners:', allData.suppliers?.filter(a => a.type == 3));
    
    setForm({
      ...property,
      owner_id: ownerId, // Set owner_id from the property's uid
      coordinates: coords
    });
    setEditingProperty(property);
    setAddOpen(true);
  };

  // Filter properties based on search
  const filteredProperties = useMemo(() => {
    if (!searchTerm.trim()) return properties;
    
    return properties.filter(property => {
      const owner = allData.suppliers?.find(a => a.id == property.uid)?.name || '';
      const searchLower = searchTerm.toLowerCase();
      
      switch (searchType) {
        case 'property_no':
          return property.property_no?.toLowerCase().includes(searchLower);
        case 'owner':
          return owner.toLowerCase().includes(searchLower);
        case 'location':
          return property.location?.toLowerCase().includes(searchLower);
        case 'all':
        default:
          return (
            property.property_no?.toLowerCase().includes(searchLower) ||
            owner.toLowerCase().includes(searchLower) ||
            property.location?.toLowerCase().includes(searchLower)
          );
      }
    });
  }, [properties, searchTerm, searchType, allData.suppliers]);

  const columns = useMemo(() => [
    { name: 'Owner', selector: row =>allData.suppliers.filter(a=>a.type == 3).find(a=>a.id == row.uid)?.name, sortable: true, wrap: true },
    { name: 'Property No', selector: row => row.property_no, sortable: true, wrap: true },
    { name: 'Type', selector: row => row.type, sortable: true },
    { name: 'Location', selector: row => row.location, sortable: true },
    { name: 'Annual Payable', selector: row => row.annual_payable, sortable: true, right: true },

    {
      name: 'Actions',
      cell: row => (
        <div className="flex gap-2">
          <button
            onClick={() => openDetails(row)}
            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
            title="View Details"
          >
            <FaEye className="text-sm" />       
          </button>
          <button
            onClick={() => handleEdit(row)}
            className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
            title="Edit Property"
          >
            <FaEdit className="text-sm" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ], [allData.suppliers]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-xl p-3">
              <FaBuilding className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Properties</h1>
              <p className="text-blue-100 text-sm mt-1">Manage property records and information</p>
            </div>
          </div>
          <button
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-blue-950 rounded-lg hover:bg-blue-50 transition-colors font-medium shadow-sm"
          >
            <FaPlus />
            <span>Add Property</span>
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          {/* Search Type Selector */}
          <div className="flex-shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <FaFilter className="text-gray-500 text-sm" />
                <span>Search by:</span>
              </div>
            </label>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">All Fields</option>
              <option value="property_no">Property Number</option>
              <option value="owner">Owner</option>
              <option value="location">Location</option>
            </select>
          </div>

          {/* Search Input */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search:</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={`Search ${searchType === 'all' ? 'properties' : searchType.replace('_', ' ')}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Search Results Count */}
          <div className="flex-shrink-0">
            <div className="bg-white px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 font-medium">
              {searchTerm ? `${filteredProperties.length} results` : `${properties.length} properties`}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FaSpinner className="animate-spin text-blue-950 text-4xl mb-4" />
            <p className="text-gray-600 font-medium">Loading properties...</p>
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <FaBuilding className="text-gray-400 text-5xl mb-4" />
            {searchTerm ? (
              <>
                <p className="text-gray-600 font-medium mb-2">No properties found matching your search</p>
                <p className="text-sm text-gray-500 mb-4">Try adjusting your search terms</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors text-sm font-medium"
                >
                  Clear Search
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-600 font-medium mb-2">No properties found</p>
                <p className="text-sm text-gray-500 mb-4">Add a property to get started</p>
                <button
                  onClick={() => setAddOpen(true)}
                  className="px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <FaPlus />
                  <span>Add Property</span>
                </button>
              </>
            )}
          </div>
        ) : (
          <DataTable
            title=""
            columns={columns}
            data={filteredProperties}
            pagination
            highlightOnHover
            striped
            responsive
            customStyles={customStyles}
          />
        )}
      </div>

      {modalOpen && (
        <Modal
          title={selectedProperty ? `Property: ${selectedProperty.property_no}` : 'Property'}
          onClose={() => setModalOpen(false)}
        >
          {selectedProperty ? (
            <PropertyDetails property={selectedProperty} allData={allData} />
          ) : null}
        </Modal>
      )}

      {addOpen && (
        <Modal
          title={editingProperty ? "Edit Property" : "Add Property"}
          onClose={() => { setAddOpen(false); resetForm(); }}
        >
          <form onSubmit={submitAdd} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Property No" name="property_no" value={form.property_no} onChange={onFormChange} required />
              <Select label="Type" name="type" value={form.type} onChange={onFormChange} options={[
                {label:'Residential', value:'RES'},
                {label:'Commercial', value:'COM'},
                {label:'Industrial', value:'IND'},
                {label:'Hospitality', value:'HOS'},
                {label:'Other', value:'OTH'}
              ]} />
              <Input label="Account Name" name="acc_name" value={form.acc_name} onChange={onFormChange} required />
              <SearchableSelect 
                label="Property Owner" 
                name="owner_id" 
                value={form.owner_id} 
                onChange={onFormChange} 
                options={allData.suppliers?.filter(a => a.type == 3).map(owner => ({
                  label: owner.name,
                  value: owner.id
                })) || []} 
                required 
              />
              <Input label="Location" name="location" value={form.location} onChange={onFormChange} />
              <Input label="Street" name="street" value={form.street} onChange={onFormChange} />
              <Input label="Description" name="description" value={form.description} onChange={onFormChange} />
              <Input label="Annual Payable" name="annual_payable" value={form.annual_payable} onChange={onFormChange} />
              <Input label="Opening" name="opening" value={form.opening} onChange={onFormChange} />
              <Input label="Rate Value" name="rate_value" value={form.rate_value} onChange={onFormChange} />
              <Input label="Land Value" name="land_value" value={form.land_value} onChange={onFormChange} />
              <Input label="Land Extent" name="land_ext" value={form.land_ext} onChange={onFormChange} />
              <Input label="Improvements" name="improvements" value={form.improvements} onChange={onFormChange} />
              <Input label="Machinery" name="machinery" value={form.machinery} onChange={onFormChange} />
              <Input label="Remarks" name="remarks" value={form.remarks} onChange={onFormChange} />
            </div>
            
            {/* Coordinate Input Section */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Property Coordinates</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({
                    ...prev,
                    coordinates: [...prev.coordinates, { lat: '', lng: '' }]
                  }))}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                >
                  <FaPlus className="text-xs" />
                  <span>Add Coordinate</span>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">Add at least 4 coordinate pairs to define the property boundary</p>
              
              <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                {form.coordinates.map((coord, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">#{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        value={coord.lat}
                        onChange={(e) => {
                          const newCoords = [...form.coordinates];
                          newCoords[index].lat = e.target.value;
                          setForm(prev => ({ ...prev, coordinates: newCoords }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        placeholder="e.g., -15.416667"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        value={coord.lng}
                        onChange={(e) => {
                          const newCoords = [...form.coordinates];
                          newCoords[index].lng = e.target.value;
                          setForm(prev => ({ ...prev, coordinates: newCoords }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        placeholder="e.g., 28.283333"
                      />
                    </div>
                    {form.coordinates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newCoords = form.coordinates.filter((_, i) => i !== index);
                          setForm(prev => ({ ...prev, coordinates: newCoords }));
                        }}
                        className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                        title="Remove coordinate"
                      >
                        <FaTimes className="text-sm" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {form.coordinates.length < 4 && (
                <div className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-center gap-2">
                  <FaTimes className="text-amber-600" />
                  <span>At least 4 coordinate pairs are recommended for property boundary</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button 
                type="button" 
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" 
                onClick={() => { setAddOpen(false); resetForm(); }}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-2.5 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50" 
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FaPlus />
                    <span>{editingProperty ? 'Update Property' : 'Add Property'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

function PropertyDetails({ property, allData }) {
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'history' | 'map'
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const coordString = property.coordinates; // expected format: "lat1,lng1;lat2,lng2;lat3,lng3;lat4,lng4" or null
  const coords = useMemo(() => {
    if (!coordString || typeof coordString !== 'string') return null;
    const coordPairs = coordString.split(';').map(pair => {
      const parts = pair.split(',').map(s => parseFloat(s.trim()));
      if (parts.length !== 2 || parts.some(isNaN)) return null;
      return { lat: parts[0], lng: parts[1] };
    }).filter(Boolean);
    
    // Need at least 4 coordinates for a polygon
    if (coordPairs.length < 4) return null;
    return coordPairs;
  }, [coordString]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    let isMounted = true;
    const fetchHistory = async () => {
      try {
        setHistoryLoading(true);
        // Attempt fetching history; adjust endpoint as needed if backend differs
        const res = await axios.get(rootUrl(`/getPropertyHistory/${property.id}`));
        if (!isMounted) return;
        setHistory(res.data.data);
        
      } catch (e) {
        if (!isMounted) return;
        setHistory([]);
      } finally {
        if (isMounted) setHistoryLoading(false);
      }
    };
    fetchHistory();
    return () => { isMounted = false; };
  }, [activeTab, property.id]);

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-2">
          <button
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all ${
              activeTab === 'details' 
                ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white shadow-md' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setActiveTab('details')}
          >
            <FaList />
            <span>Details</span>
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all ${
              activeTab === 'history' 
                ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white shadow-md' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setActiveTab('history')}
          >
            <FaList />
            <span>History</span>
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all ${
              activeTab === 'map' 
                ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white shadow-md' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } ${!coords ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => setActiveTab('map')}
            disabled={!coords}
            title={!coords ? 'No coordinates available' : ''}
          >
            <FaMapMarkerAlt />
            <span>Map</span>
          </button>
        </div>
      </div>

      {activeTab === 'details' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoRow label="Property No" value={property.property_no} icon={FaBuilding} />
          <InfoRow label="Owner" value={allData.suppliers.find(a=>a.id == property.uid)?.name} icon={FaUser} />
          <InfoRow label="Type" value={property.type} />
          <InfoRow label="Account" value={property.acc_name} />
          <InfoRow label="COA" value={property.coa} />
          <InfoRow label="Description" value={property.description} />
          <InfoRow label="Location" value={property.location} icon={FaMapMarkerAlt} />
          <InfoRow label="Street" value={property.street} />
          <InfoRow label="Annual Payable" value={property.annual_payable} icon={FaDollarSign} />
          <InfoRow label="Opening" value={property.opening} />
          <InfoRow label="Rate Value" value={property.rate_value} />
          <InfoRow label="Land Value" value={property.land_value} />
          <InfoRow label="Land Extent" value={property.land_ext} />
          <InfoRow label="Improvements" value={property.improvements} />
          <InfoRow label="Machinery" value={property.machinery} />
          <InfoRow label="Remarks" value={property.remarks} />
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          {historyLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FaSpinner className="animate-spin text-blue-950 text-4xl mb-4" />
              <p className="text-gray-600 font-medium">Loading history...</p>
            </div>
          ) : !history || (!history.bills && !history.receipts) ? (
            <div className="text-center py-12 text-gray-500">
              <FaList className="text-4xl mx-auto mb-4 text-gray-400" />
              <p>No history available</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Combined Transaction History Table */}
              <div className="overflow-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Date</th>
                      <th className="px-4 py-3 text-left font-semibold">Type</th>
                      <th className="px-4 py-3 text-left font-semibold">Reference</th>
                      <th className="px-4 py-3 text-left font-semibold">Description</th>
                      <th className="px-4 py-3 text-right font-semibold">Debit</th>
                      <th className="px-4 py-3 text-right font-semibold">Credit</th>
                      <th className="px-4 py-3 text-right font-semibold">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const openingBalance = parseFloat(property.opening || 0);
                      
                      // Create combined transactions array
                      const transactions = [];
                      
                      // Add opening balance as first entry
                      if (openingBalance !== 0) {
                        transactions.push({
                          id: 'opening',
                          date: new Date(0), // Very early date to sort first
                          type: 'Opening Balance',
                          reference: '-',
                          description: 'Opening Balance',
                          amount: openingBalance,
                          isDebit: openingBalance > 0,
                          isReceipt: false
                        });
                      }
                      
                      // Add bills (debits)
                      if (history.bills) {
                        history.bills.forEach(bill => {
                          transactions.push({
                            id: `bill_${bill.id}`,
                            date: new Date(bill.date),
                            type: 'Bill',
                            reference: bill.invoice_no || '-',
                            description: bill.description || '-',
                            amount: parseFloat(bill.amount || 0),
                            isDebit: true,
                            isReceipt: false
                          });
                        });
                      }
                      
                      // Add receipts (credits)
                      if (history.receipts) {
                        history.receipts.forEach(receipt => {
                          transactions.push({
                            id: `receipt_${receipt.id}`,
                            date: new Date(receipt.date),
                            type: 'Receipt',
                            reference: receipt.receipt_no || '-',
                            description: receipt.description || '-',
                            amount: parseFloat(receipt.amount || 0),
                            isDebit: false,
                            isReceipt: true
                          });
                        });
                      }
                      
                      // Sort by date
                      transactions.sort((a, b) => a.date - b.date);
                      
                      // Calculate running balance
                      let runningBalance = 0;
                      
                      return transactions.map((transaction, idx) => {
                        // Calculate running balance
                        if (transaction.isDebit) {
                          runningBalance += transaction.amount;
                        } else {
                          runningBalance -= transaction.amount;
                        }
                        
                        return (
                          <tr key={transaction.id} className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <td className="px-4 py-3 text-gray-700">
                              {transaction.id === 'opening' ? 'Opening' : transaction.date.toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                transaction.type === 'Opening Balance' 
                                  ? 'bg-gray-200 text-gray-700'
                                  : transaction.isReceipt 
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                              }`}>
                                {transaction.type}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">{transaction.reference}</td>
                            <td className="px-4 py-3 text-gray-700">{transaction.description}</td>
                            <td className="px-4 py-3 text-right font-medium text-red-600">
                              {transaction.isDebit ? `ZMW ${transaction.amount.toFixed(2)}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-green-600">
                              {!transaction.isDebit ? `ZMW ${transaction.amount.toFixed(2)}` : '-'}
                            </td>
                            <td className={`px-4 py-3 text-right font-bold ${
                              runningBalance >= 0 ? 'text-red-600' : 'text-green-600'
                            }`}>
                              ZMW {runningBalance.toFixed(2)}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Summary Section */}
              <div className="bg-gradient-to-r from-blue-50 to-gray-50 p-6 rounded-xl border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaDollarSign className="text-blue-600" />
                  <span>Summary</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                    <div className="text-sm text-gray-600 mb-1">Opening Balance</div>
                    <div className="text-xl font-bold text-gray-900">
                      ZMW {parseFloat(property.opening || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                    <div className="text-sm text-gray-600 mb-1">Total Bills</div>
                    <div className="text-xl font-bold text-red-600">
                      ZMW {history.bills ? history.bills.reduce((sum, bill) => sum + parseFloat(bill.amount || 0), 0).toFixed(2) : '0.00'}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                    <div className="text-sm text-gray-600 mb-1">Total Receipts</div>
                    <div className="text-xl font-bold text-green-600">
                      ZMW {history.receipts ? history.receipts.reduce((sum, receipt) => sum + parseFloat(receipt.amount || 0), 0).toFixed(2) : '0.00'}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                    <div className="text-sm text-gray-600 mb-1">Current Balance</div>
                    <div className={`text-xl font-bold ${
                      (() => {
                        const opening = parseFloat(property.opening || 0);
                        const totalBills = history.bills ? history.bills.reduce((sum, bill) => sum + parseFloat(bill.amount || 0), 0) : 0;
                        const totalReceipts = history.receipts ? history.receipts.reduce((sum, receipt) => sum + parseFloat(receipt.amount || 0), 0) : 0;
                        const balance = opening + totalBills - totalReceipts;
                        return balance >= 0 ? 'text-red-600' : 'text-green-600';
                      })()
                    }`}>
                      ZMW {(() => {
                        const opening = parseFloat(property.opening || 0);
                        const totalBills = history.bills ? history.bills.reduce((sum, bill) => sum + parseFloat(bill.amount || 0), 0) : 0;
                        const totalReceipts = history.receipts ? history.receipts.reduce((sum, receipt) => sum + parseFloat(receipt.amount || 0), 0) : 0;
                        return (opening + totalBills - totalReceipts).toFixed(2);
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'map' && (
        coords ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <FaMapMarkerAlt className="text-blue-600" />
              <span>Property Location</span>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <LeafletMap coords={coords} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <FaMapMarkerAlt className="text-4xl mb-4 text-gray-400" />
            <p>No coordinates available for map</p>
            <p className="text-sm mt-1">Add coordinates to view property location</p>
          </div>
        )
      )}
    </div>
  );
}




function Input({ label, name, value, onChange, type = 'text', required = false, placeholder }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-gray-700 mb-1.5">{label} {required && <span className="text-red-500">*</span>}</div>
      <input
        className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        required={required}
        placeholder={placeholder}
      />
    </label>
  );
}

function Select({ label, name, value, onChange, options = [] }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-gray-700 mb-1.5">{label}</div>
      <select
        className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
        name={name}
        value={value}
        onChange={onChange}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </label>
  );
}

function SearchableSelect({ label, name, value, onChange, options = [], required = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);

  // Find selected option
  useEffect(() => {
    const selected = options.find(opt => opt.value == value); // Use == for type coercion
    setSelectedOption(selected);
    // Debug logging
    if (value && !selected) {
      console.log('Owner not found for value:', value, 'Available options:', options);
    }
  }, [value, options]);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    setSelectedOption(option);
    onChange({ target: { name, value: option.value } });
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className="relative">
      <label className="block">
        <div className="text-sm font-medium text-gray-700 mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex items-center justify-between bg-white transition-colors"
          >
            <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
              {selectedOption ? selectedOption.label : `Select ${label.toLowerCase()}`}
            </span>
            <svg
              className={`w-4 h-4 transition-transform text-gray-500 ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Search owners..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 transition-colors ${
                        option.value === value ? 'bg-blue-50 text-blue-900 font-medium' : 'text-gray-900'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    No owners found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </label>
      
      {/* Backdrop to close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
          }}
        />
      )}
    </div>
  );
}

function InfoRow({ label, value, icon: Icon }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div className="flex items-center gap-2 text-xs font-medium text-gray-600 mb-1.5">
        {Icon && <Icon className="text-gray-500" />}
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-gray-900 font-medium">{value ?? '-'}</div>
    </div>
  );
}

function LeafletMap({ coords }) {
  const mapRef = useRef(null);
  const mapId = useMemo(() => `map_${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    let mapInstance;

    const ensureLeafletLoaded = () => new Promise((resolve, reject) => {
      if (window.L) return resolve(window.L);

      const existingCss = document.querySelector('link[data-leaflet]');
      const existingJs = document.querySelector('script[data-leaflet]');

      if (!existingCss) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.setAttribute('data-leaflet', 'true');
        document.head.appendChild(link);
      }

      if (existingJs) {
        existingJs.addEventListener('load', () => resolve(window.L));
        existingJs.addEventListener('error', reject);
      } else {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.defer = true;
        script.setAttribute('data-leaflet', 'true');
        script.onload = () => resolve(window.L);
        script.onerror = reject;
        document.body.appendChild(script);
      }
    });

    ensureLeafletLoaded().then((L) => {
      if (!mapRef.current || !coords || coords.length < 4) return;
      
      // Calculate bounds for the polygon
      const lats = coords.map(c => c.lat);
      const lngs = coords.map(c => c.lng);
      const bounds = L.latLngBounds(
        L.latLng(Math.min(...lats), Math.min(...lngs)),
        L.latLng(Math.max(...lats), Math.max(...lngs))
      );
      
      mapInstance = L.map(mapRef.current).fitBounds(bounds);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(mapInstance);
      
      // Create polygon from coordinates
      const polygonCoords = coords.map(coord => [coord.lat, coord.lng]);
      const polygon = L.polygon(polygonCoords, {
        color: 'blue',
        fillColor: 'lightblue',
        fillOpacity: 0.3,
        weight: 2
      }).addTo(mapInstance);
      
      // Add markers at each corner
      coords.forEach((coord, index) => {
        L.marker([coord.lat, coord.lng])
          .addTo(mapInstance)
          .bindPopup(`Point ${index + 1}: ${coord.lat.toFixed(6)}, ${coord.lng.toFixed(6)}`);
      });
    });

    return () => {
      if (mapInstance) {
        mapInstance.remove();
      }
    };
  }, [coords]);

  return (
    <div id={mapId} ref={mapRef} className="w-full h-64 rounded-md border" />
  );
}

export default Properties